const MIN_ATTRIBUTES = 3;

const ApartmentMatchmaker = {
    init() {
        document.querySelectorAll('[data-apartment-matchmaker]').forEach((root) => {
            const dom = this.cacheDom(root);
            if (!dom) return;

            this.bindEvents(dom);
            this.syncMatchButton(dom);
        });
    },

    cacheDom(root) {
        const match = root.querySelector('[data-match]');
        const selectPhase = root.querySelector('[data-select-phase]');
        const processingPhase = root.querySelector('[data-processing-phase]');
        const resultsPhase = root.querySelector('[data-results-phase]');
        if (!match || !selectPhase || !processingPhase || !resultsPhase) return null;

        return {
            root,
            match,
            selectPhase,
            processingPhase,
            resultsPhase,
            attributes: root.querySelectorAll('[data-attribute]'),
            restart: root.querySelector('[data-button-restart]'),
            resultImage: root.querySelector('[data-result-image]'),
            resultTitle: root.querySelector('[data-result-title]'),
            resultTitleSvg: root.querySelector('[data-result-title-svg]'),
            resultDescription: root.querySelector('[data-result-description]'),
            resultLink: root.querySelector('[data-result-link]'),
            resultLinkLabel: root.querySelector('[data-result-link-label]'),
            result: root.querySelector('[data-result]'),
            alsoLike: root.querySelector('[data-also-like]'),
            intro: root.querySelector('.apartment-matchmaker__intro'),
            titleSvgs: this.parseTitleSvgs(root),
            endpoint: root.dataset.matchEndpoint || '',
            nonce: root.dataset.nonce || '',
        };
    },

    parseTitleSvgs(root) {
        const dataEl = root.querySelector('[data-property-titles]');
        if (!dataEl) return {};

        try {
            const parsed = JSON.parse(dataEl.textContent);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
            return {};
        }
    },

    getTitleSvg(dom, slug) {
        if (!slug || !dom.titleSvgs) return '';
        return dom.titleSvgs[slug] || '';
    },

    bindEvents(dom) {
        dom.attributes.forEach((button) => {
            button.addEventListener('click', () => {
                const pressed = button.getAttribute('aria-pressed') === 'true';
                button.setAttribute('aria-pressed', pressed ? 'false' : 'true');
                this.syncMatchButton(dom);
            });
        });

        dom.match.addEventListener('click', () => this.onMatch(dom));

        if (dom.restart) {
            dom.restart.addEventListener('click', () => {
                this.setPhase(dom, 'select');
                dom.attributes.forEach((button) => {
                    button.setAttribute('aria-pressed', 'false');
                });
                this.syncMatchButton(dom);
            });
        }
    },

    getSelected(dom) {
        return Array.from(dom.attributes)
            .filter((button) => button.getAttribute('aria-pressed') === 'true')
            .map((button) => button.dataset.attribute)
            .filter(Boolean);
    },

    syncMatchButton(dom) {
        dom.match.disabled = this.getSelected(dom).length < MIN_ATTRIBUTES;
        dom.match.textContent = dom.match.disabled ? 'Choose at least 3' : 'Match Me!';
    },

    setPhase(dom, phase) {
        const scrollTop = this.getIntroScrollTop(dom);

        dom.selectPhase.hidden = phase !== 'select';
        dom.processingPhase.hidden = phase !== 'processing';
        dom.resultsPhase.hidden = phase !== 'results';

        requestAnimationFrame(() => {
            this.scrollToModule(scrollTop);
        });
    },

    getIntroScrollTop(dom) {
        const target = dom.intro || dom.root;
        const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        return Math.max(0, window.scrollY + target.getBoundingClientRect().bottom - padding);
    },

    scrollToModule(top) {
        const html = document.documentElement;
        const previous = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo({ top, left: 0, behavior: 'instant' });
        html.style.scrollBehavior = previous;
    },

    async onMatch(dom) {
        const attributes = this.getSelected(dom);
        if (attributes.length < MIN_ATTRIBUTES || !dom.endpoint) return;

        this.setPhase(dom, 'processing');
        dom.match.disabled = true;

        const delay = 3000 + Math.floor(Math.random() * 1001);
        const wait = new Promise((resolve) => setTimeout(resolve, delay));

        try {
            const [result] = await Promise.all([this.fetchMatch(dom, attributes), wait]);
            this.renderResult(dom, result);
            this.setPhase(dom, 'results');
        } catch (e) {
            this.setPhase(dom, 'select');
            this.syncMatchButton(dom);
        }
    },

    async fetchMatch(dom, attributes) {
        const headers = {
            'Content-Type': 'application/json',
        };

        if (dom.nonce) {
            headers['X-WP-Nonce'] = dom.nonce;
        }

        const response = await fetch(dom.endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({ attributes }),
        });

        if (!response.ok) {
            throw new Error('Match failed');
        }

        return response.json();
    },

    renderResult(dom, result) {
        if (dom.result) {
            const theme = result.theme_color || '';
            dom.result.dataset.theme = theme;
        }

        if (dom.resultTitle) {
            const svg = this.getTitleSvg(dom, result.slug);
            dom.resultTitle.textContent = result.title || '';
            dom.resultTitle.hidden = Boolean(svg);
        }

        if (dom.resultTitleSvg) {
            const svg = this.getTitleSvg(dom, result.slug);
            if (svg) {
                dom.resultTitleSvg.innerHTML = svg;
                const svgEl = dom.resultTitleSvg.querySelector('svg');
                if (svgEl) {
                    svgEl.setAttribute('aria-hidden', 'true');
                    svgEl.setAttribute('focusable', 'false');
                }
                dom.resultTitleSvg.hidden = false;
            } else {
                dom.resultTitleSvg.replaceChildren();
                dom.resultTitleSvg.hidden = true;
            }
        }

        if (dom.resultDescription) {
            dom.resultDescription.innerHTML = result.description || '';
        }

        if (dom.resultImage) {
            if (result.image && result.image.url) {
                dom.resultImage.src = result.image.url;
                dom.resultImage.alt = result.image.alt || '';
                dom.resultImage.hidden = false;
            } else {
                dom.resultImage.removeAttribute('src');
                dom.resultImage.alt = '';
                dom.resultImage.hidden = true;
            }
        }

        if (dom.resultLink) {
            if (result.url) {
                dom.resultLink.href = result.url;
                dom.resultLink.hidden = false;
                if (dom.resultLinkLabel) {
                    dom.resultLinkLabel.textContent = result.title
                        ? `Visit ${result.title}`
                        : 'Visit';
                }
                if (result.is_external) {
                    dom.resultLink.target = '_blank';
                    dom.resultLink.rel = 'noopener noreferrer';
                } else {
                    dom.resultLink.removeAttribute('target');
                    dom.resultLink.removeAttribute('rel');
                }
            } else {
                dom.resultLink.href = '#';
                dom.resultLink.hidden = true;
            }
        }

        this.renderAlsoLike(dom, result.alternatives || []);
    },

    renderAlsoLike(dom, alternatives) {
        if (!dom.alsoLike) return;

        const items = alternatives.map((property) => {
            const item = document.createElement('li');
            const svg = this.getTitleSvg(dom, property.slug);
            const media = document.createElement('span');
            media.className = 'apartment-matchmaker__also-like-title';

            if (svg) {
                media.innerHTML = svg;
                const svgEl = media.querySelector('svg');
                if (svgEl) {
                    svgEl.setAttribute('aria-hidden', 'true');
                    svgEl.setAttribute('focusable', 'false');
                }
            } else {
                media.textContent = property.title || '';
            }

            if (property.url) {
                const link = document.createElement('a');
                link.href = property.url;
                if (property.is_external) {
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                }
                if (property.title) {
                    link.setAttribute('aria-label', property.title);
                }
                link.appendChild(media);
                item.appendChild(link);
            } else {
                item.appendChild(media);
            }

            return item;
        });

        const wrap = dom.alsoLike.closest('.apartment-matchmaker__also-like');
        if (wrap) {
            wrap.hidden = items.length === 0;
        }

        dom.alsoLike.replaceChildren(...items);
    },
};

export default ApartmentMatchmaker;
